import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { getCookie } from '@sportyano/core/common/utils/cookies-controller';
import { permission, UserType } from '@sportyano/core/models/permissions/permission';

// Interfaces for type safety
export interface Voucher {
  id?: number;
  name: string;
  value: number;
  expiry_date?: string;
  type: string;
  voucherable_id: number;
  voucherable_type?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  currency_code?: string|null;
}

export interface Program {
  id: number;
  name: string;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  code: number;
}

export interface ApiErrorResponse {
  message: string;
  code: number;
}

import { AuthService } from "@sportyano/core/services/authServices/auth.service";

@Injectable({
  providedIn: 'root'
})
export class VouchersService {

  private apiUrl = environment.baseUrl;
  private _translate = inject(TranslateService);
  private _authService = inject(AuthService);

  constructor(private http: HttpClient) { }


  /**
   * Get all vouchers by type
   * @param type - The voucher type (e.g., 'program')
   * @returns Observable of vouchers array
   */
  getVouchers(type: string): Observable<ApiResponse<Voucher[]>> {
      const userType = this._authService.getUserType();
      if(userType == permission.branch){
        return this.http.get<ApiResponse<Voucher[]>>(
          `${this.apiUrl}branch-user/vouchers/${type}`);
      }
      else {
        return this.http.get<ApiResponse<Voucher[]>>(
          `${this.apiUrl}vouchers/${type}`);
      }
  }

  /**
   * Create a new voucher
   * @param voucher - The voucher data to create
   * @returns Observable of created voucher
   */
  createVoucher(voucher: Voucher): Observable<ApiResponse<Voucher>> {
      const userType = this._authService.getUserType();
      if(userType == permission.branch){
          return this.http.post<ApiResponse<Voucher>>(
            `${this.apiUrl}branch-user/store-voucher`,
            voucher    );
      }
      return this.http.post<ApiResponse<Voucher>>(
        `${this.apiUrl}store-voucher`,
        voucher    );
  }

  /**
   * Update an existing voucher
   * @param id - The voucher ID
   * @param voucher - The voucher data to update
   * @returns Observable of updated voucher
   */
  updateVoucher(id: number, voucher: Partial<Voucher>): Observable<ApiResponse<Voucher>> {
      const userType = this._authService.getUserType();
      if(userType == permission.branch){
            return this.http.put<ApiResponse<Voucher>>(
              `${this.apiUrl}branch-user/update-voucher/${id}`,
              voucher    
            );
      }
      return this.http.put<ApiResponse<Voucher>>(
        `${this.apiUrl}update-voucher/${id}`,
        voucher    
      );
  }

  /**
   * Delete a voucher
   * @param id - The voucher ID
   * @returns Observable of delete response
   */
  deleteVoucher(id: number): Observable<ApiResponse<null>> {
      const userType = this._authService.getUserType();
      if(userType == permission.branch){
          return this.http.delete<ApiResponse<null>>(
          `${this.apiUrl}branch-user/delete-voucher/${id}`    );
      }
      return this.http.delete<ApiResponse<null>>(
        `${this.apiUrl}delete-voucher/${id}`    );
  }
	/**
	 * Check if a voucher is valid by name
	 * @param name - The voucher name to check
	 * @returns Observable of voucher data or error response
	 */

	checkVoucher(name: string, ids: number[]): Observable<ApiResponse<Voucher>> {
		const params = new URLSearchParams();
		ids.forEach(id => params.append('ids[]', id.toString()));

		return this.http.get<ApiResponse<Voucher>>(
		  `${this.apiUrl}check-voucher/${name}?${params.toString()}`
		);
	  }


}